import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { statusCountsSchema } from '../../../lib/statusCounts';
import { isAssignableShigoRole, ShigoRole } from '../../auth/permissions';
import {
  AdminUsersContext,
  AdminUserStatus,
  adminUserMutationResponseSchema,
  adminUserNetworkSchema,
  adminUserPublisherSchema,
  adminUserSchema,
  adminUsersPageSchema,
  normalizeRegularAdminUsersPage,
  type AdminUser,
  type AdminUserMutationPayload,
  type AdminUsersFilter,
} from './model';

type AdminUsersClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
};

const emptyMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.void(),
);
const publishersPageSchema = z.looseObject({
  content: z.array(adminUserPublisherSchema),
  totalElements: z.number().int().nonnegative(),
});

export function createAdminUsersClient({ config, accessToken, fetcher }: AdminUsersClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    getStatusCounts: (context: AdminUsersContext) =>
      api.request({
        path:
          context === AdminUsersContext.SuperAdmin
            ? '/api/admin/users/statuses'
            : `/api/users/statuses?${toSearchParams({ networkId: config.networkId })}`,
        schema: statusCountsSchema.transform((counts) => ({
          [AdminUserStatus.Active]: counts['enabled'] ?? 0,
          [AdminUserStatus.Archived]: counts['disabled'] ?? 0,
        })),
      }),
    list: async (context: AdminUsersContext, filter: AdminUsersFilter) => {
      if (context === AdminUsersContext.SuperAdmin) {
        return api.request({
          path: `/api/sa/users?${serializeAdminUsersFilter(filter, context)}`,
          schema: adminUsersPageSchema,
        });
      }

      const users = await api.request({
        path: `/api/users/list-all?${serializeAdminUsersFilter(filter, context, config.networkId)}`,
        schema: z.array(adminUserSchema),
      });
      return normalizeRegularAdminUsersPage(users, filter);
    },
    create: async (context: AdminUsersContext, payload: AdminUserMutationPayload) => {
      return api.request({
        path: context === AdminUsersContext.SuperAdmin ? '/api/sa/users' : '/api/users',
        schema: adminUserMutationResponseSchema,
        init: jsonInit('POST', payload),
      });
    },
    update: async (
      context: AdminUsersContext,
      user: AdminUser,
      payload: AdminUserMutationPayload,
    ) => {
      const superAdmin = context === AdminUsersContext.SuperAdmin;
      const userId = user.id?.trim();
      if (superAdmin && !userId) {
        throw new Error('A user ID is required to update a Super Admin user.');
      }
      return api.request({
        path: superAdmin
          ? `/api/sa/users/${encodeURIComponent(userId ?? '')}`
          : `/api/users/${encodeURIComponent(user.email)}`,
        schema: adminUserMutationResponseSchema,
        init: jsonInit('PUT', payload),
      });
    },
    setEnabled: (context: AdminUsersContext, username: string, enabled: boolean) =>
      api.request({
        path: `${
          context === AdminUsersContext.SuperAdmin ? '/api/admin/users' : '/api/users'
        }/${encodeURIComponent(username)}/${enabled ? 'enable' : 'disable'}`,
        schema: emptyMutationResponseSchema,
        init: jsonInit('PUT', {}),
      }),
    listNetworks: () =>
      api.request({
        path: '/api/admin/networks/list-all',
        schema: z.array(adminUserNetworkSchema),
      }),
    listAllowedRoles: (networkId: string) =>
      api.request({
        path: `/api/admin/networks/${encodeURIComponent(networkId)}/roles`,
        schema: z
          .array(z.enum(ShigoRole))
          .transform((roles) => roles.filter(isAssignableShigoRole)),
      }),
    listPublishers: async (context: AdminUsersContext, networkId: string) => {
      const pageSize = 1000;
      const content: z.infer<typeof adminUserPublisherSchema>[] = [];
      let page = 0;
      let totalElements = 0;

      do {
        const response = await api.request({
          path: `${
            context === AdminUsersContext.SuperAdmin ? '/api/sa/publisher' : '/api/publishers'
          }?${toSearchParams({
            page,
            size: pageSize,
            sortProperty: 'name',
            sortDirection: 'ASC',
            status: 'ACTIVE',
            networkIds: networkId || undefined,
          })}`,
          schema: publishersPageSchema,
        });
        content.push(...response.content);
        totalElements = response.totalElements;
        page += 1;

        if (response.content.length === 0) break;
      } while (content.length < totalElements);

      return { content, totalElements };
    },
  };
}

export function serializeAdminUsersFilter(
  filter: AdminUsersFilter,
  context: AdminUsersContext,
  networkId?: string,
) {
  const enabled = resolveEnabledFilter(filter.statuses);
  const superAdmin = context === AdminUsersContext.SuperAdmin;
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    ...(!superAdmin && networkId ? { networkId } : {}),
    searchTerm: filter.searchTerm,
    ...(superAdmin && filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    enabled,
    userRole: filter.role || undefined,
  }).toString();
}

function resolveEnabledFilter(statuses: AdminUserStatus[]) {
  if (statuses.length !== 1) return undefined;
  return statuses[0] === AdminUserStatus.Active;
}
